import React, {useState, useRef, useEffect} from 'react'
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import apiClient from "../../request/http-common";
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import { CardActions } from '@mui/material';
import TextField from '@mui/material/TextField';
import { CardContent } from '@mui/material';
import Card from '@mui/material/Card';
import { useMutation } from 'react-query';
import { setFundings } from '../Wallet/walletSlice';
import {
  CardHeader, 
  Divider, 
  Paper, 
  Container, 
  Chip, 
  Avatar, 
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
  IconButton,
  InputAdornment,
  Badge,
  CircularProgress
} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid';
import { styled, alpha } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatIcon from '@mui/icons-material/Chat';
import ImageIcon from '@mui/icons-material/Image';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoIcon from '@mui/icons-material/Info';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoadingButton from '@mui/lab/LoadingButton';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  backgroundColor: status === 'successful' 
    ? alpha(theme.palette.success.main, 0.1)
    : alpha(theme.palette.primary.main, 0.1),
  color: status === 'successful'
    ? theme.palette.success.dark
    : theme.palette.primary.dark,
  '& .MuiChip-icon': {
    color: 'inherit'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    height: 26,
  }
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.25rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    color: theme.palette.primary.main
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  }
}));

const GradientDivider = styled(Divider)(({ theme }) => ({
  height: '3px',
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`
}));

const AnimatedButton = styled(LoadingButton)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: '8px',
  padding: '10px 24px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
  },
}));

const CompactPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  }
}));

const ResponsiveBox = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  height: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  }
}));

const ResponsiveAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  width: 56,
  height: 56,
  [theme.breakpoints.down('sm')]: {
    width: 42,
    height: 42,
  }
}));

const CopyLinkField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1),
    backgroundColor: alpha(theme.palette.background.default, 0.7),
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const ChatMessageBubble = styled(Box)(({ theme, isUser }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2),
  maxWidth: '80%',
  wordBreak: 'break-word',
  marginBottom: theme.spacing(1),
  position: 'relative',
  ...(isUser ? {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.text.primary,
    borderTopRightRadius: theme.spacing(0.5),
    alignSelf: 'flex-end',
  } : {
    backgroundColor: alpha(theme.palette.primary.main, 0.7),
    color: theme.palette.common.white,
    borderTopLeftRadius: theme.spacing(0.5),
    alignSelf: 'flex-start',
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.5),
    fontSize: '0.85rem',
  }
}));

const ChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
  borderRadius: theme.spacing(1.5),
  overflow: 'hidden',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const ChatMessagesContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: alpha(theme.palette.common.black, 0.05),
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.3),
    }
  }
}));

const ChatInputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  }
}));

const MessageTime = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  display: 'block',
  textAlign: 'right',
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  minHeight: 50,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 1.5),
    minHeight: 42,
  }
}));

const SystemMessage = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(1),
  margin: theme.spacing(1, 0),
  fontSize: '0.8rem',
  color: theme.palette.text.secondary,
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  borderRadius: theme.spacing(1),
  width: 'fit-content',
  alignSelf: 'center',
}));

const MessageBubble = styled(Box)(({ theme, isUser, isSystem, pending }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2),
  maxWidth: '80%',
  wordBreak: 'break-word',
  marginBottom: theme.spacing(1),
  position: 'relative',
  opacity: pending ? 0.7 : 1,
  ...(isSystem ? {
    backgroundColor: alpha(theme.palette.info.light, 0.1),
    color: theme.palette.text.secondary,
    borderRadius: theme.spacing(1),
    maxWidth: '90%',
    alignSelf: 'center',
    fontSize: '0.8rem',
    fontStyle: 'italic'
  } : isUser ? {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.text.primary,
    borderTopRightRadius: theme.spacing(0.5),
    alignSelf: 'flex-end',
  } : {
    backgroundColor: alpha(theme.palette.primary.main, 0.7),
    color: theme.palette.common.white,
    borderTopLeftRadius: theme.spacing(0.5),
    alignSelf: 'flex-start',
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.5),
    fontSize: '0.85rem',
  }
}));

const MessageText = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
  }
}));

const SystemMessageContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.8rem',
  color: theme.palette.text.secondary,
}));

const Transaction = () => {
  const params = useParams();
  const [error, setErrors] = useState('');
  const formatPrice = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencySign: 'standard' }).format(amount);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [amount, setAmount] = useState(null);
  const [buttonText, setButtonText] = useState(null);
  const fundings = useSelector((state) => state.wallet.fundings);
          const pmtLink = `https://dashboard.hulacapital.com/payment/${params.id}`;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Chat related states
  const [message, setMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [loadingChat, setLoadingChat] = useState(false);
  
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Fetch the funding details
  const funding = fundings.find(f => parseInt(f.id) === parseInt(params.id) && f.advertiser !== null);

  // Check if a chat exists or create one if needed
  useEffect(() => {
    if (funding) {
      setLoadingChat(true);
      
      // First check if a chat already exists for this funding
      apiClient.get(`/api/funding/chat/by-funding/${funding.id}`)
        .then(response => {
          if (response.data.status === 'success') {
            setChatId(response.data.chat_id);
            fetchChatMessages(response.data.chat_id);
          } else {
            // Create a new chat for this funding
            apiClient.post('/api/funding/chat/start', {
              payment_method: funding.payment_method || 'Bank Transfer',
              funding_id: funding.id
            })
            .then(response => {
              if (response.data.status === 'success') {
                setChatId(response.data.chat_id);
                // Add a system message
                setChatMessages([{
                  id: 1,
                  text: 'Transaction has been initiated. You can chat with the other party here.',
                  timestamp: new Date(),
                  isSystem: true
                }]);
              }
              setLoadingChat(false);
            })
            .catch(error => {
              console.error('Error creating chat:', error);
              setLoadingChat(false);
            });
          }
        })
        .catch(error => {
          console.error('Error checking for existing chat:', error);
          setLoadingChat(false);
        });
    }
  }, [funding]);

  // Function to fetch chat messages
  const fetchChatMessages = (chatId) => {
    setLoadingChat(true);
    apiClient.get(`/api/funding/chat/messages/${chatId}`)
      .then(response => {
        if (response.data.status === 'success') {
          // Transform API messages to our format
          const formattedMessages = response.data.messages.map((msg, index) => ({
            id: index + 1,
            text: msg.message,
            sender: msg.sender,
            timestamp: new Date(msg.created_at || Date.now()),
            isUser: msg.direction === 'outgoing'
          }));
          
          // Add system message if no messages exist
          if (formattedMessages.length === 0) {
            formattedMessages.unshift({
              id: 0,
              text: 'Transaction has been initiated. You can chat with the other party here.',
              timestamp: new Date(),
              isSystem: true
            });
          }
          
          setChatMessages(formattedMessages);
        }
        setLoadingChat(false);
      })
      .catch(error => {
        console.error('Error fetching chat messages:', error);
        setLoadingChat(false);
      });
  };

  // Send message mutation
  const { mutate: sendMessageMutation, isLoading: isSendingMessage } = useMutation(
    async (messageText) => {
      return await apiClient.post('/api/funding/chat/message', {
        chat_id: chatId,
        message: messageText
      });
    },
    {
      onSuccess: (response) => {
        // Reload messages after sending
        fetchChatMessages(chatId);
      },
      onError: (error) => {
        console.error('Error sending message:', error);
        // Show error notification
        localStorage.setItem('notification', JSON.stringify({
          message: 'Failed to send message. Please try again.',
          type: 'error'
        }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
      }
    }
  );
  
  const handleSendMessage = () => {
    if (message.trim() && chatId) {
      // Optimistically add message to UI
      const newMessage = {
        id: chatMessages.length + 1,
        text: message,
        sender: 'user',
        timestamp: new Date(),
        isUser: true,
        pending: true
      };
      setChatMessages([...chatMessages, newMessage]);
      
      // Send to API
      sendMessageMutation(message.trim());
      
      // Clear input field
      setMessage('');
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Poll for new messages every 5 seconds if chat exists
  useEffect(() => {
    if (chatId) {
      const interval = setInterval(() => {
        fetchChatMessages(chatId);
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [chatId]);

  const { isLoading: isSendingRequest, mutate: postRequest } = useMutation(
    async () => {
      return await apiClient.post(`/api/confirm-funding`, {
        amount: amount,
        coin: params.id
      });
    },
    {
      onSuccess: (res) => {
        // Add a system message about the confirmation
        if (chatId) {
          apiClient.post('/api/funding/chat/message', {
            chat_id: chatId,
            message: 'Payment has been confirmed by the buyer.',
            is_system: true
          }).catch(error => console.error('Error sending system message:', error));
        }
        
        dispatch(setFundings(res.data.fundings));
        localStorage.setItem('notification', JSON.stringify({message : `${res.data.message}`, 'type': 'success' }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
        const event2 = new Event('processed');
        window.dispatchEvent(event2);
        setTimeout(() => {
          navigate('/account-wallet');
        }, 2000);
      },
      onError: (err) => { 
        if (err.response?.data.length) {
          let myerror = err.response?.data || err;         
          const event2 = new Event('processed');
          window.dispatchEvent(event2); 
        } else {
          let errMessage = err.response?.data.message || err;     
          localStorage.setItem('notification', JSON.stringify({message : `${errMessage}`, 'type': 'error' }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          const event2 = new Event('processed');
          window.dispatchEvent(event2); 
        }
      },
    }
  );

  if (!funding) {
    return (
      <Container maxWidth="lg" disableGutters={isMobile}>
        <ResponsiveBox
          sx={{
            mt: { xs: 7, sm: 9 },
            mb: { xs: 3, sm: 5 }
          }}
        >
          <Alert severity="error" sx={{ borderRadius: theme.spacing(1) }}>
            Transaction not found or invalid
          </Alert>
        </ResponsiveBox>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="lg" 
      disableGutters 
      sx={{ 
        height: { 
          xs: 'calc(100vh - 56px)', // Mobile header height
          sm: 'calc(100vh - 64px)'  // Desktop header height
        }, 
        overflow: 'hidden', 
        mt: { 
          xs: '56px',  // Mobile header height
          sm: '64px'   // Desktop header height
        },
        pb: 2 // Add some bottom padding for safety
      }}
    >
      <ResponsiveBox
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <StyledCard elevation={0} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            sx={{ 
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1.5 }
            }}
            avatar={
              <ResponsiveAvatar sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                <ChatIcon fontSize="small" />
              </ResponsiveAvatar>
            }
            title={
              <CardTitle variant="h5" component="div" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Transaction Chat
              </CardTitle>
            }
            action={
              <StatusChip 
                label={funding.status} 
                color={funding.status === 'successful' ? 'success' : 'primary'}
                icon={funding.status === 'successful' ? <CheckCircleIcon /> : null}
              />
            }
          />
          <GradientDivider />
          <CardContent sx={{ p: { xs: 1, sm: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <ChatContainer sx={{ flexGrow: 1 }}>
              <ChatHeader>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexGrow: 1, flexWrap: 'wrap', overflow: 'hidden' }}>
                  <Avatar 
                    sx={{ 
                      width: { xs: 24, sm: 28 }, 
                      height: { xs: 24, sm: 28 }, 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      mt: 0.5
                    }}
                  >
                    {funding.advertiser.trader_name[0].toUpperCase()}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="subtitle2" noWrap sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                        {funding.advertiser.trader_name}
                      </Typography>
                      <Tooltip title="Verified trader">
                        <VerifiedIcon fontSize="small" color="primary" sx={{ fontSize: '0.8rem', flexShrink: 0 }} />
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                      {formatPrice(funding.amount)} • ID: #{funding.id} • {funding.advertiser.wallet_type === 'bank' ? 'Bank Transfer' : 'Crypto Payment'}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  size="small" 
                  label="Active Now" 
                  color="success" 
                  sx={{ 
                    height: 20, 
                    '& .MuiChip-label': { 
                      px: 1,
                      fontSize: '0.65rem'
                    },
                    flexShrink: 0,
                    ml: 0.5
                  }} 
                />
              </ChatHeader>
              
              <ChatMessagesContainer>
                {loadingChat && chatMessages.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  chatMessages.map((msg) => (
                    <MessageBubble
                      key={msg.id}
                      isUser={msg.isUser}
                      isSystem={msg.isSystem}
                      pending={msg.pending}
                    >
                      {msg.isSystem ? (
                        <SystemMessageContent>
                          <InfoIcon fontSize="small" sx={{ mr: 1 }} />
                          {msg.text}
                        </SystemMessageContent>
                      ) : (
                        <>
                          <MessageText>{msg.text}</MessageText>
                          <MessageTime>
                            {msg.pending ? (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <AccessTimeIcon sx={{ fontSize: '0.75rem', mr: 0.5 }} />
                                Sending...
                              </Box>
                            ) : (
                              new Date(msg.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            )}
                          </MessageTime>
                        </>
                      )}
                    </MessageBubble>
                  ))
                )}
                <div ref={messagesEndRef} />
              </ChatMessagesContainer>
              
              <ChatInputContainer>
                <FormControl fullWidth variant="outlined">
                  <OutlinedInput
                    placeholder="Type a message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={!chatId || isSendingMessage}
                    multiline
                    maxRows={2}
                    endAdornment={
                      <InputAdornment position="end">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton
                            aria-label="send message"
                            onClick={handleSendMessage}
                            disabled={!message.trim() || !chatId || isSendingMessage}
                            sx={{ 
                              p: 0.5,
                              backgroundColor: message.trim() && chatId ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
                            }}
                          >
                            {isSendingMessage ? <CircularProgress size={20} /> : <SendIcon fontSize="small" />}
                          </IconButton>
                        </Box>
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.background.paper, 0.5),
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.divider, 0.2),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      }
                    }}
                  />
                </FormControl>
                
                {funding.status !== 'successful' && (
                  <Box mt={2} display="flex" justifyContent="center">
                    <AnimatedButton
                      onClick={postRequest}
                      variant="contained"
                      color="primary"
                      loading={isSendingRequest}
                      loadingPosition="center"
                      fullWidth
                      size="medium"
                      startIcon={<CheckCircleIcon />}
                      sx={{
                        maxWidth: { xs: '100%', sm: '400px' }
                      }}
                    >
                      {isSendingRequest ? 'Processing...' : 'Confirm Payment'}
                    </AnimatedButton>
                  </Box>
                )}
              </ChatInputContainer>
            </ChatContainer>
          </CardContent>
        </StyledCard>
      </ResponsiveBox>
    </Container>
  );
};

export default Transaction;
